import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
// import axios from "axios";
// import { useDispatch } from "react-redux";
import { TextInput } from "../../../../form/textInput";
import { Select } from "../../../../form/select";
import { updateNodeValue } from "../../../../../services/method";
import { operationType, selectDataType } from "../../../../../services/types";
import { Main, Styled } from "./style";
import { Textarea } from "../../../../form/textarea";
// import { reduxUpdateError } from "../../../../../redux/reducers/error";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { AuthenticateSelect } from "../../../../auth/select";
import { Switch } from "../../../../form/switch";

type WordPressProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function WordPress({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: WordPressProps) {
  const connectionRequired = React.useMemo(
    () =>
      new Set([
        "create_post",
        "update_post",
        "delete_post",
        "create_page",
        "update_page",
        "delete_page",
        "create_user",
        "update_user",
        "delete_user",
      ]),
    []
  );

  const [requests, setRequests] = React.useState<selectDataType[]>([
    { id: 2, name: "List Posts", refId: "list_posts", isActive: true },
    { id: 1, name: "Get Post", refId: "get_post" },
    { id: 3, name: "Create Post", refId: "create_post" },
    { id: 4, name: "Update Post", refId: "update_post" },
    { id: 5, name: "Delete Post", refId: "delete_post" },
    { id: 7, name: "List Pages", refId: "list_pages" },
    { id: 6, name: "Get Page", refId: "get_page" },
    { id: 8, name: "Create Page", refId: "create_page" },
    { id: 9, name: "Update Page", refId: "update_page" },
    { id: 10, name: "Delete Page", refId: "delete_page" },
    { id: 12, name: "List Users", refId: "list_users" },
    { id: 11, name: "Get User", refId: "get_user" },
    { id: 13, name: "Create User", refId: "create_user" },
    { id: 14, name: "Update User", refId: "update_user" },
    { id: 15, name: "Delete User", refId: "delete_user" },
    { id: 16, name: "Let AI Agent Decide", refId: "agent_decide" },
  ]);
  const [status, setStatus] = React.useState<selectDataType[]>([
    {
      id: 1,
      name: "Publish (public and visible)",
      refId: "publish",
      isActive: true,
    },
    { id: 2, name: "Draft (saved but not published)", refId: "draft" },
    { id: 3, name: "Pending (awaiting review)", refId: "pending" },
    { id: 4, name: "Private (admins/editors)", refId: "private" },
    { id: 5, name: "Future (scheduled post)", refId: "future" },
  ]);
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hasConnection, setHasConnection] = React.useState<boolean>(
    !!data.value?.connection
  );

  React.useEffect(() => {
    setHasConnection(!!data.value?.connection);
  }, [data.value?.connection]);

  // Normalize connection data whenever a new connection is attached
  React.useEffect(() => {
    if (!data.value?.connection) return;

    const connection = data.value.connection;
    const wpBaseUrl =
      connection.wp_base_url ||
      connection.base_url ||
      connection.web_url ||
      connection.url;
    const authUsername =
      connection.authUsername || connection.username || connection.user;
    const authPassword =
      connection.authPassword ||
      connection.app_password ||
      connection.password ||
      connection.pass;
    const directToken =
      connection.token ||
      connection.authToken ||
      connection.access_token ||
      connection.accessToken;
    const token =
      directToken ||
      (authUsername || authPassword
        ? `${authUsername || ""}:${authPassword || ""}`
        : undefined);

    const patch: Record<string, any> = {};
    if (wpBaseUrl && data.value?.wp_base_url !== wpBaseUrl)
      patch.wp_base_url = wpBaseUrl;
    if (authUsername && data.value?.authUsername !== authUsername)
      patch.authUsername = authUsername;
    if (authPassword && data.value?.authPassword !== authPassword)
      patch.authPassword = authPassword;
    if (token && data.value?.token !== token) patch.token = token;

    if (Object.keys(patch).length) updateNodeValue(id, setNodes, patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.connection?.id]);

  // --
  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  // ---
  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        request: requests[0].refId,
        status: status[0].refId,
      });
    } else {
      setRequests((requests) =>
        requests.map((request) => ({
          ...request,
          isActive: request.refId === data.value.request,
        }))
      );
      setStatus((status) =>
        status.map((status) => ({
          ...status,
          isActive: status.refId === data.value.status,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---
  const handleAuthenticate = (
    type: "add" | "remove",
    connection?: { [key: string]: any }
  ) => {
    if (type === "add" && connection) {
      const wpBaseUrl =
        connection.wp_base_url || connection.base_url || connection.web_url|| connection.url;
      const authUsername = connection.authUsername || connection.username;
      const authPassword =
        connection.authPassword ||
        connection.app_password ||
        connection.password;
      const token =
        connection.token ||
        (authUsername || authPassword
          ? `${authUsername || ""}:${authPassword || ""}`
          : undefined);

      const payload: Record<string, any> = { connection };
      if (wpBaseUrl) payload.wp_base_url = wpBaseUrl;
      if (authUsername) payload.authUsername = authUsername;
      if (authPassword) payload.authPassword = authPassword;
      if (token) payload.token = token;

      updateNodeValue(id, setNodes, payload);

      setHasConnection(true);
    } else if (type === "remove") {
      setRequests((requests) =>
        requests.map((request, index) => ({
          ...request,
          isActive: index === 0,
        }))
      );
      updateNodeValue(id, setNodes, {
        connection: null,
        request: requests[0].refId,
      });
      setHasConnection(false);
    }
  };

  // ---
  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    keyRef?: string
  ) => {
    if (!keyRef) return;

    const value = e.target.value;
    if (["authUsername", "authPassword"].includes(keyRef)) {
      const username =
        keyRef === "authUsername" ? value : data.value?.authUsername;
      const appPassword =
        keyRef === "authPassword" ? value : data.value?.authPassword;
      const token =
        username || appPassword ? `${username || ""}:${appPassword || ""}` : "";
      updateNodeValue(id, setNodes, { [keyRef]: value, token });
    } else {
      updateNodeValue(id, setNodes, { [keyRef]: value });
    }
  };

  // ---
  const handleSelectRequests = (
    action: "request" | "status",
    selectId: number
  ) => {
    if (action === "request") {
      const refId = requests.find((obj) => obj.id === selectId)?.refId;
      if (!data.value?.connection && connectionRequired.has(refId || ""))
        return;

      setRequests((requests) =>
        requests.map((request) => {
          if (request.id === selectId) return { ...request, isActive: true };
          else return { ...request, isActive: false };
        })
      );
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                value: { ...node.data.value, request: refId },
              },
            };
          } else return node;
        })
      );
    } else if (action === "status") {
      setStatus((status) =>
        status.map((status) => {
          if (status.id === selectId) return { ...status, isActive: true };
          else return { ...status, isActive: false };
        })
      );
      updateNodeValue(id, setNodes, {
        status: status.find((obj) => obj.id === selectId)?.refId,
      });
    }
  };

  // ---
  const isWarning = false;
  const filteredRequests = React.useMemo(
    () =>
      requests.map((req) => ({
        ...req,
        isHide: !hasConnection && connectionRequired.has(req.refId || ""),
      })),
    [requests, hasConnection, connectionRequired]
  );

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}
        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "380px" }}>
            <Styled>
              <AuthenticateSelect
                type="wordpress"
                defaultId={data.value?.connection?.id}
                handleAuthenticate={handleAuthenticate}
                background={data.background}
                withLine
              />

              {!hasConnection ? (
                <TextInput
                  keyRef="wp_base_url"
                  value={data.value?.wp_base_url}
                  title="Base URL"
                  placeholder="https://site.com"
                  handleChange={handleChangeInput}
                />
              ) : (
                <div className="authRow">
                  <button
                    className="authButton remove btn1"
                    onClick={() => handleAuthenticate("remove")}>
                    Remove Connection
                  </button>
                </div>
              )}
              <Select
                title="Request"
                data={filteredRequests}
                handleSelectItem={(id) => handleSelectRequests("request", id)}
                isSmall={true}
              />

              {[
                "get_post",
                "update_post",
                "delete_post",
                "get_page",
                "delete_page",
                "update_page",
                "get_user",
                "delete_user",
                "update_user",
              ].includes(data.value?.request) ? (
                <TextInput
                  keyRef="postId"
                  value={data.value?.postId}
                  title="ID"
                  placeholder="1"
                  handleChange={handleChangeInput}
                />
              ) : null}

              {[
                "create_post",
                "update_post",
                "create_page",
                "update_page",
              ].includes(data.value?.request) ? (
                <>
                  <Select
                    title="Status"
                    data={status}
                    handleSelectItem={(id) =>
                      handleSelectRequests("status", id)
                    }
                    isSmall={true}
                  />
                  <TextInput
                    keyRef="title"
                    value={data.value?.title}
                    title="Title"
                    placeholder="title"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="slug"
                    value={data.value?.slug}
                    title="Slug"
                    placeholder="custom-slug"
                    handleChange={handleChangeInput}
                  />
                </>
              ) : null}

              {data.value?.request === "create_post" ? (
                <>
                  <TextInput
                    keyRef="categories"
                    value={data.value?.categories}
                    title="Categories"
                    placeholder="1,2,3"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="tags"
                    value={data.value?.tags}
                    title="Tags"
                    placeholder="4,5,6"
                    handleChange={handleChangeInput}
                  />
                </>
              ) : null}

              {[
                "create_post",
                "update_post",
                "create_page",
                "update_page",
              ].includes(data.value?.request) ? (
                <>
                  <Textarea
                    keyRef="any"
                    value={data.value?.any}
                    placeholder="Content"
                    handleChange={handleChangeInput}
                  />
                </>
              ) : null}

                {/* {["create_user", "update_user"].includes(
                data.value?.request || ""
              ) ? (
                <>
                </>
              ) : null} */}

              {["create_user", "update_user"].includes(data.value?.request) ? (
                <>
                <TextInput
                  keyRef="username"
                  value={data.value?.username}
                  title="Username"
                  placeholder={
                    data.value?.request === "create_user"
                      ? "username"
                      : "new username"
                  }
                  handleChange={handleChangeInput}
                />
                  <TextInput
                    keyRef="email"
                    value={data.value?.email}
                    title="Email"
                    placeholder="new email"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="first_name"
                    value={data.value?.first_name}
                    title="First name"
                    placeholder="new first name"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="last_name"
                    value={data.value?.last_name}
                    title="Last name"
                    placeholder="new last name"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="password"
                    value={data.value?.password}
                    title="Password"
                    placeholder="new password"
                    handleChange={handleChangeInput}
                  />
                </>
              ) : null}

              {data.value?.request === "delete_user" ? (
                <>
                  <TextInput
                    keyRef="reassign"
                    value={data.value?.reassign}
                    title="Reassign ID"
                    placeholder="1"
                    handleChange={handleChangeInput}
                  />
                </>
              ) : null}
              <Switch
                title="Fetch only new items"
                value={!!data.value?.fetchOnlyNew}
                setValue={() =>
                  updateNodeValue(id, setNodes, {
                    fetchOnlyNew: !data.value?.fetchOnlyNew,
                  })
                }
                className="autopilot"
              />
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}